import type { GeoTIFFImage } from "geotiff";

import {
  buildWriterGeoMetadata,
  COG_DETAIL_TILE_CACHE_LIMIT,
  COG_DETAIL_TILE_CONCURRENCY,
  COG_DETAIL_TILE_SIZE_PX,
  COG_SOURCE_BLOCK_CACHE_BUDGET_BYTES,
  ensureProjDef,
  getEpsgCode,
  getTileLngLatBounds,
  rastersToObjectUrl,
  toInterleavedRasters,
  VIEWPORT_TILE_BUFFER_RATIO,
  wgs84ToPixelCoords,
  windowToCoordinates,
  type CogFullResWindow,
  type CogGeoTransform,
  type CogOrthoApi,
  type CogOrthoMemoryStats,
  type CogTiff,
} from "./cogOrtho";
import { fromCogUrl } from "./fromCogUrl";
import { readJsHeapSnapshot } from "./jsHeapMemory";
import type {
  LngLatBoundsLike,
  OrthoDetailTile,
  OrthoImageOverlay,
} from "./orthoAssets";
import {
  readCachedOrthoImages,
  writeCachedOrthoImage,
} from "./orthoImageCache";

export interface CogOrthoSessionState {
  detailAccessOrder: string[];
  detailBlobBytes: Map<string, number>;
  detailCache: Map<string, OrthoDetailTile>;
  detailCachePeakBlobBytes: number;
  detailPinnedIds: Set<string>;
  image: GeoTIFFImage | null;
  orthoId: number | null;
  overview: OrthoImageOverlay | null;
  overviewBlobBytes: number;
  tiff: CogTiff | null;
  transform: CogGeoTransform | null;
  url: string | null;
}

export const createCogOrthoSessionState = (): CogOrthoSessionState => ({
  detailAccessOrder: [],
  detailBlobBytes: new Map(),
  detailCache: new Map(),
  detailCachePeakBlobBytes: 0,
  detailPinnedIds: new Set(),
  image: null,
  orthoId: null,
  overview: null,
  overviewBlobBytes: 0,
  tiff: null,
  transform: null,
  url: null,
});

export const disposeCogOrthoSessionState = (
  state: CogOrthoSessionState,
): void => {
  if (state.overview != null) {
    URL.revokeObjectURL(state.overview.imageUrl);
  }
  for (const tile of state.detailCache.values()) {
    URL.revokeObjectURL(tile.imageUrl);
  }
  const cleared = createCogOrthoSessionState();
  Object.assign(state, cleared);
};

export const createCogOrthoSessionApi = (
  getState: () => CogOrthoSessionState,
): CogOrthoApi => {
  const requireTransform = (): CogGeoTransform => {
    const transform = getState().transform;
    if (transform == null) {
      throw new Error("CogOrtho is not open");
    }
    return transform;
  };

  const getDetailCacheBlobBytes = (): number => {
    let total = 0;
    for (const bytes of getState().detailBlobBytes.values()) {
      total += bytes;
    }
    return total;
  };

  const touchDetailTile = (tileId: string): void => {
    const state = getState();
    state.detailAccessOrder = state.detailAccessOrder.filter(
      (id) => id !== tileId,
    );
    state.detailAccessOrder.push(tileId);
  };

  const evictDetailTilesIfNeeded = (
    maxTiles: number = COG_DETAIL_TILE_CACHE_LIMIT,
  ): void => {
    const state = getState();
    while (
      state.detailCache.size > maxTiles &&
      state.detailAccessOrder.length > 0
    ) {
      const oldestIndex = state.detailAccessOrder.findIndex(
        (tileId) => !state.detailPinnedIds.has(tileId),
      );
      if (oldestIndex === -1) {
        break;
      }
      const oldestId = state.detailAccessOrder.splice(oldestIndex, 1)[0];
      const tile = state.detailCache.get(oldestId);
      if (tile == null) {
        continue;
      }
      URL.revokeObjectURL(tile.imageUrl);
      state.detailCache.delete(oldestId);
      state.detailBlobBytes.delete(oldestId);
    }
  };

  const recordDetailCachePeak = (): void => {
    const state = getState();
    const current = getDetailCacheBlobBytes();
    if (current > state.detailCachePeakBlobBytes) {
      state.detailCachePeakBlobBytes = current;
      console.debug(
        "[CogOrthoProvider] detail tile cache peak",
        JSON.stringify({
          detailCachePeakBlobBytes: state.detailCachePeakBlobBytes,
          detailCacheSize: state.detailCache.size,
          jsHeap: readJsHeapSnapshot(),
        }),
      );
    }
  };

  const getMemoryStats = (): CogOrthoMemoryStats => {
    const state = getState();
    return {
      detailCacheBlobBytes: getDetailCacheBlobBytes(),
      detailCacheLimit: COG_DETAIL_TILE_CACHE_LIMIT,
      detailCachePeakBlobBytes: state.detailCachePeakBlobBytes,
      detailCacheSize: state.detailCache.size,
      geotiffBlockCacheBudgetBytes: COG_SOURCE_BLOCK_CACHE_BUDGET_BYTES,
      jsHeap: readJsHeapSnapshot(),
      overviewBlobBytes: state.overviewBlobBytes,
    };
  };

  const open = async (): Promise<void> => {
    const state = getState();
    if (state.tiff != null) {
      return;
    }
    if (state.url == null) {
      throw new Error("CogOrtho has no URL");
    }

    console.debug("[CogOrthoProvider] fromUrl start");
    const tiff = await fromCogUrl(state.url);
    (tiff as unknown as { cache: boolean }).cache = true;
    const image = await tiff.getImage();

    const geoKeys = image.getGeoKeys();
    if (geoKeys == null) {
      throw new Error("COG is missing geo keys");
    }

    const epsgCode = getEpsgCode(geoKeys);
    ensureProjDef(epsgCode);

    const [originX, originY] = image.getOrigin();
    const [resolutionX, resolutionY] = image.getResolution();

    const transform: CogGeoTransform = {
      epsgCode,
      height: image.getHeight(),
      originX,
      originY,
      resolutionX,
      resolutionY,
      samplesPerPixel: image.getSamplesPerPixel(),
      width: image.getWidth(),
    };

    state.tiff = tiff;
    state.image = image;
    state.transform = transform;

    console.debug(
      "[CogOrthoProvider] open complete",
      JSON.stringify({
        epsgCode: transform.epsgCode,
        geoKeys,
        height: transform.height,
        originX: transform.originX,
        originY: transform.originY,
        resolutionX: transform.resolutionX,
        resolutionY: transform.resolutionY,
        samplesPerPixel: transform.samplesPerPixel,
        width: transform.width,
      }),
    );
  };

  const listDetailTileIds = (): string[] => {
    const transform = getState().transform;
    if (transform == null) {
      return [];
    }

    const cols = Math.ceil(transform.width / COG_DETAIL_TILE_SIZE_PX);
    const rows = Math.ceil(transform.height / COG_DETAIL_TILE_SIZE_PX);
    const ids: string[] = [];
    for (let row = 0; row < rows; row += 1) {
      for (let col = 0; col < cols; col += 1) {
        ids.push(`${col}_${row}`);
      }
    }
    return ids;
  };

  const getDetailTileWindow = (
    tileId: string,
  ): {
    col: number;
    row: number;
    x0: number;
    x1: number;
    y0: number;
    y1: number;
  } => {
    const transform = requireTransform();
    const [colText, rowText] = tileId.split("_");
    const col = Number(colText);
    const row = Number(rowText);
    if (Number.isNaN(col) || Number.isNaN(row)) {
      throw new Error(`Invalid COG tile id: ${tileId}`);
    }

    const x0 = col * COG_DETAIL_TILE_SIZE_PX;
    const y0 = row * COG_DETAIL_TILE_SIZE_PX;
    return {
      col,
      row,
      x0,
      x1: Math.min(transform.width, x0 + COG_DETAIL_TILE_SIZE_PX),
      y0,
      y1: Math.min(transform.height, y0 + COG_DETAIL_TILE_SIZE_PX),
    };
  };

  const getCachedDetailTile = (tileId: string): OrthoDetailTile | null => {
    const cached = getState().detailCache.get(tileId);
    if (cached == null) {
      return null;
    }
    touchDetailTile(tileId);
    return cached;
  };

  const pinDetailTiles = (tileIds: Iterable<string>): void => {
    const state = getState();
    state.detailPinnedIds = new Set(tileIds);
    for (const tileId of state.detailPinnedIds) {
      touchDetailTile(tileId);
    }
    evictDetailTilesIfNeeded();
  };

  const loadDetailTile = async (tileId: string): Promise<OrthoDetailTile> => {
    const cached = getCachedDetailTile(tileId);
    if (cached != null) {
      return cached;
    }

    const state = getState();
    const orthoId = state.orthoId;
    const transform = state.transform;
    const image = state.image;
    if (transform == null || image == null) {
      throw new Error("CogOrtho is not open");
    }

    const { x0, x1, y0, y1 } = getDetailTileWindow(tileId);
    const width = x1 - x0;
    const height = y1 - y0;
    const startedAt = performance.now();
    console.debug(
      "[CogOrthoProvider] loadDetailTile network",
      JSON.stringify({ height, tileId, width, window: [x0, y0, x1, y1] }),
    );
    const rasters = await image.readRasters({
      height,
      interleave: true,
      width,
      window: [x0, y0, x1, y1],
    });

    const { blob, byteLength, imageUrl } = await rastersToObjectUrl(
      rasters,
      width,
      height,
      transform.samplesPerPixel,
    );
    const coordinates = windowToCoordinates(transform, x0, y0, x1, y1);
    if (orthoId != null) {
      void writeCachedOrthoImage({
        blob,
        coordinates,
        height,
        kind: "detail",
        orthoId,
        tileId,
        transform,
        width,
      });
    }
    const tile: OrthoDetailTile = {
      coordinates,
      file: tileId,
      id: tileId,
      imageUrl,
    };
    state.detailCache.set(tileId, tile);
    state.detailBlobBytes.set(tileId, byteLength);
    touchDetailTile(tileId);
    evictDetailTilesIfNeeded();
    recordDetailCachePeak();
    console.debug(
      "[CogOrthoProvider] loadDetailTile done",
      JSON.stringify({
        blobBytes: byteLength,
        cacheSize: state.detailCache.size,
        coordinates,
        elapsedMs: performance.now() - startedAt,
        memory: getMemoryStats(),
        tileId,
      }),
    );
    return tile;
  };

  const hydratePersistedDetailTiles = async (
    tileIds: string[],
  ): Promise<{ hits: OrthoDetailTile[]; misses: string[] }> => {
    if (tileIds.length === 0) {
      return { hits: [], misses: [] };
    }

    const orthoId = getState().orthoId;
    if (orthoId == null) {
      return { hits: [], misses: tileIds };
    }

    const persistedById = await readCachedOrthoImages(orthoId, tileIds);
    let state: CogOrthoSessionState;
    try {
      state = getState();
    } catch {
      return { hits: [], misses: tileIds };
    }
    if (state.orthoId !== orthoId) {
      return { hits: [], misses: tileIds };
    }

    const hits: OrthoDetailTile[] = [];
    const misses: string[] = [];
    for (const tileId of tileIds) {
      const persisted = persistedById.get(tileId);
      if (persisted == null) {
        misses.push(tileId);
        continue;
      }

      const alreadyCached = state.detailCache.get(tileId);
      if (alreadyCached != null) {
        hits.push(alreadyCached);
        continue;
      }

      const tile: OrthoDetailTile = {
        coordinates: persisted.coordinates,
        file: tileId,
        id: tileId,
        imageUrl: URL.createObjectURL(persisted.blob),
      };
      state.detailCache.set(tileId, tile);
      state.detailBlobBytes.set(tileId, persisted.byteLength);
      touchDetailTile(tileId);
      hits.push(tile);
    }

    evictDetailTilesIfNeeded();
    recordDetailCachePeak();
    return { hits, misses };
  };

  return {
    getCachedDetailTile,
    getDetailCacheSize: () => getState().detailCache.size,
    getMemoryStats,
    getWriterGeoMetadata: (windowX0, windowY0, width, height) => {
      const image = getState().image;
      if (image == null) {
        throw new Error("CogOrtho is not open");
      }
      return buildWriterGeoMetadata(
        image,
        requireTransform(),
        windowX0,
        windowY0,
        width,
        height,
      );
    },
    listVisibleDetailTileIds: (
      mapBounds: LngLatBoundsLike,
      bufferRatio: number = VIEWPORT_TILE_BUFFER_RATIO,
    ) => {
      const transform = getState().transform;
      if (transform == null) {
        return [];
      }

      const west = mapBounds.getWest();
      const east = mapBounds.getEast();
      const south = mapBounds.getSouth();
      const north = mapBounds.getNorth();
      const lngPad = (east - west) * bufferRatio;
      const latPad = (north - south) * bufferRatio;
      const query = {
        east: east + lngPad,
        north: north + latPad,
        south: south - latPad,
        west: west - lngPad,
      };
      const centerLng = (query.west + query.east) / 2;
      const centerLat = (query.south + query.north) / 2;

      const allIds = listDetailTileIds();
      const visibleIds = allIds
        .map((tileId) => {
          const { x0, x1, y0, y1 } = getDetailTileWindow(tileId);
          const coordinates = windowToCoordinates(transform, x0, y0, x1, y1);
          const tileBounds = getTileLngLatBounds(coordinates);
          const intersects = !(
            tileBounds.east < query.west ||
            tileBounds.west > query.east ||
            tileBounds.north < query.south ||
            tileBounds.south > query.north
          );
          if (!intersects) {
            return null;
          }
          const tileCenterLng = (tileBounds.west + tileBounds.east) / 2;
          const tileCenterLat = (tileBounds.south + tileBounds.north) / 2;
          const distSq =
            (tileCenterLng - centerLng) ** 2 + (tileCenterLat - centerLat) ** 2;
          return { distSq, tileId };
        })
        .filter(
          (entry): entry is { distSq: number; tileId: string } => entry != null,
        )
        .sort((a, b) => a.distSq - b.distSq)
        .map((entry) => entry.tileId);

      console.debug(
        "[CogOrthoProvider] visible detail tiles",
        JSON.stringify({
          bufferRatio,
          cachedCount: getState().detailCache.size,
          gridCount: allIds.length,
          imageHeight: transform.height,
          imageWidth: transform.width,
          query,
          tileSizePx: COG_DETAIL_TILE_SIZE_PX,
          visibleCount: visibleIds.length,
          visibleIds: visibleIds.slice(0, 24),
          visibleIdsTruncated: visibleIds.length > 24,
        }),
      );

      return visibleIds;
    },
    loadDetailTilesById: async (
      tileIds,
      { concurrency = COG_DETAIL_TILE_CONCURRENCY, onTile, shouldContinue },
    ) => {
      const pendingIds: string[] = [];
      let cacheHits = 0;

      for (const tileId of tileIds) {
        if (shouldContinue?.() === false) {
          return { cacheHits, cacheMisses: pendingIds.length };
        }
        const cached = getCachedDetailTile(tileId);
        if (cached != null) {
          cacheHits += 1;
          onTile(cached);
        } else {
          pendingIds.push(tileId);
        }
      }

      const persisted = await hydratePersistedDetailTiles(pendingIds);
      if (shouldContinue?.() === false) {
        return {
          cacheHits,
          cacheMisses: pendingIds.length,
        };
      }
      for (const tile of persisted.hits) {
        cacheHits += 1;
        onTile(tile);
      }
      const missIds = persisted.misses;

      console.debug(
        "[CogOrthoProvider] detail tile cache lookup",
        JSON.stringify({
          cacheHits,
          cacheMisses: missIds.length,
          cacheSize: getState().detailCache.size,
          persistedHits: persisted.hits.length,
          pinnedCount: getState().detailPinnedIds.size,
          requested: tileIds.length,
        }),
      );

      if (missIds.length === 0) {
        pinDetailTiles(tileIds);
        return { cacheHits, cacheMisses: 0 };
      }

      // IndexedDB missed at least one window. Open the COG and decode those.
      await open();

      let nextIndex = 0;
      const loaders = Array.from(
        { length: Math.max(1, concurrency) },
        async () => {
          while (nextIndex < missIds.length) {
            if (shouldContinue?.() === false) {
              return;
            }
            const currentIndex = nextIndex;
            nextIndex += 1;
            const tileId = missIds[currentIndex];
            if (tileId == null) {
              return;
            }
            const tile = await loadDetailTile(tileId);
            if (shouldContinue?.() === false) {
              return;
            }
            onTile(tile);
          }
        },
      );
      await Promise.all(loaders);
      pinDetailTiles(tileIds);
      return { cacheHits, cacheMisses: missIds.length };
    },
    open,
    pinDetailTiles,
    readFullResWindow: async (x0, y0, x1, y1): Promise<CogFullResWindow> => {
      await open();
      const state = getState();
      const image = state.image;
      const transform = state.transform;
      if (image == null || transform == null) {
        throw new Error("CogOrtho is not open");
      }

      const clampedX0 = Math.max(0, Math.min(transform.width, Math.floor(x0)));
      const clampedY0 = Math.max(0, Math.min(transform.height, Math.floor(y0)));
      const clampedX1 = Math.max(0, Math.min(transform.width, Math.ceil(x1)));
      const clampedY1 = Math.max(0, Math.min(transform.height, Math.ceil(y1)));
      const width = clampedX1 - clampedX0;
      const height = clampedY1 - clampedY0;
      if (width <= 0 || height <= 0) {
        throw new Error(
          `Empty COG window: [${clampedX0}, ${clampedY0}, ${clampedX1}, ${clampedY1}]`,
        );
      }

      const rasters = await image.readRasters({
        height,
        interleave: true,
        width,
        window: [clampedX0, clampedY0, clampedX1, clampedY1],
      });

      return {
        height,
        rasters: toInterleavedRasters(rasters),
        samplesPerPixel: transform.samplesPerPixel,
        width,
        x0: clampedX0,
        x1: clampedX1,
        y0: clampedY0,
        y1: clampedY1,
      };
    },
    wgs84ToPixel: (lng, lat) =>
      wgs84ToPixelCoords(requireTransform(), lng, lat),
  };
};
